import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button,
  CircularProgress, Tab, Tabs, IconButton, Tooltip, Modal,
  Snackbar, Alert, TextField, InputAdornment, LinearProgress,
  GlobalStyles, Divider,
} from '@mui/material';
import {
  Refresh, FileDownload, Fullscreen, FullscreenExit, Close,
  Search, FlashOn, WbSunny, Air, Grain, Circle,
  NavigateBefore, NavigateNext, ZoomIn,
} from '@mui/icons-material';

/* ═══════════════════════════════════════════ CONSTANTS ══ */

const SDO_WAVELENGTHS = [
  { label: '171Å',    key: '0171',  url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_0171.jpg',  desc: 'Corona – Active Regions' },
  { label: '304Å',    key: '0304',  url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_0304.jpg',  desc: 'Upper Chromosphere' },
  { label: '1600Å',   key: '1600',  url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_1600.jpg', desc: 'Transition Region' },
  { label: 'HMI MAG', key: 'HMIBC', url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_HMIBC.jpg', desc: 'Magnetic Field' },
  { label: 'HMI INT', key: 'HMII',  url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_HMII.jpg',  desc: 'Continuum Intensity' },
];

const SOHO_VIEWS = [
  { label: 'LASCO C2',  key: 'c2',      url: 'https://soho.nascom.nasa.gov/data/realtime/c2/512/latest.jpg',      fov: '6.0 R☉',  color: '#4fc3f7' },
  { label: 'LASCO C3',  key: 'c3',      url: 'https://soho.nascom.nasa.gov/data/realtime/c3/512/latest.jpg',      fov: '30.0 R☉', color: '#81d4fa' },
  { label: 'EIT 195',   key: 'eit_195', url: 'https://soho.nascom.nasa.gov/data/realtime/eit_195/512/latest.jpg', fov: '1.5 R☉',  color: '#4caf50' },
  { label: 'EIT 284',   key: 'eit_284', url: 'https://soho.nascom.nasa.gov/data/realtime/eit_284/512/latest.jpg', fov: '1.5 R☉',  color: '#ab47bc' },
];

const HUBBLE_FILTERS = ['All', 'Nebula', 'Galaxy', 'Star', 'Solar System'];

/* ════════════════════════════════════════════ HELPERS ══ */

const fmtCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const flareFromFlux = (f) => {
  if (!f || f <= 0) return 'A1.0';
  if (f >= 1e-4) return `X${(f / 1e-4).toFixed(1)}`;
  if (f >= 1e-5) return `M${(f / 1e-5).toFixed(1)}`;
  if (f >= 1e-6) return `C${(f / 1e-6).toFixed(1)}`;
  if (f >= 1e-7) return `B${(f / 1e-7).toFixed(1)}`;
  return `A${(f / 1e-8).toFixed(1)}`;
};

const flareColor = (cls) => {
  if (!cls || cls === '--') return '#90caf9';
  const c = cls[0];
  return c === 'X' ? '#ef5350' : c === 'M' ? '#ffa726' : c === 'C' ? '#ffd740' : c === 'B' ? '#4fc3f7' : '#66bb6a';
};

const downloadImage = async (url, filename) => {
  try {
    const r = await fetch(url, { mode: 'cors' });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  } catch {
    window.open(url, '_blank');
  }
};

const fmtTime = (d = new Date()) =>
  d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const mkEntry = (url, label = '') => ({
  url: `${url}?t=${Date.now()}`,
  baseUrl: url,
  timestamp: new Date(),
  label,
});

/* ═══════════════════════════════════ SHARED SMALL COMPONENTS ══ */

function LiveBadge() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5,
      bgcolor: 'rgba(239,83,80,0.12)', border: '1px solid rgba(239,83,80,0.3)', borderRadius: 2 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef5350',
        animation: 'tp-pulse 1.4s ease-in-out infinite' }} />
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#ef5350',
        fontFamily: "'Orbitron', monospace", letterSpacing: 2 }}>LIVE</Typography>
    </Box>
  );
}

function CountdownChip({ seconds, refreshing }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {refreshing
        ? <CircularProgress size={10} sx={{ color: '#4fc3f7' }} />
        : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4fc3f7',
            animation: 'tp-pulse 2s ease-in-out infinite' }} />}
      <Typography variant="caption" sx={{ color: '#4fc3f7',
        fontFamily: "'Orbitron', monospace", fontSize: 10 }}>
        {refreshing ? 'REFRESHING…' : `Next: ${fmtCountdown(seconds)}`}
      </Typography>
    </Box>
  );
}

function StatCard({ icon, label, value, color, unit, loading }) {
  return (
    <Card sx={{ bgcolor: '#0d0d0d', border: `1px solid ${color}22`, borderRadius: 2, height: '100%',
      transition: 'all 0.3s', '&:hover': { border: `1px solid ${color}55`, boxShadow: `0 0 20px ${color}18` } }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase',
            letterSpacing: 1.5, fontSize: 10 }}>{label}</Typography>
        </Box>
        {loading
          ? <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
          : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color,
                fontFamily: "'Orbitron', monospace" }}>{value}</Typography>
              {unit && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{unit}</Typography>}
            </Box>
          )}
      </CardContent>
    </Card>
  );
}

function ErrPlaceholder({ msg = 'Image temporarily unavailable', onRetry }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      width: '100%', aspectRatio: '1/1', bgcolor: 'rgba(10,22,40,0.8)',
      border: '1px solid rgba(255,255,255,0.2)', borderRadius: 2, gap: 1.5, py: 4 }}>
      <Typography sx={{ fontSize: 40 }}>🔭</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', px: 2 }}>{msg}</Typography>
      {onRetry && (
        <Button size="small" variant="outlined" onClick={onRetry}
          sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light', fontSize: 11 }}>
          Retry
        </Button>
      )}
    </Box>
  );
}

function HistoryStrip({ history, selectedIndex, onSelect, onLatest, label }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && selectedIndex === 0) ref.current.scrollLeft = 0; }, [selectedIndex]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase',
          letterSpacing: 1.5, fontSize: 10 }}>
          Image History · {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#4fc3f7',
            fontFamily: "'Orbitron', monospace", fontSize: 10 }}>
            {history.length > 0 ? `${selectedIndex + 1} of ${history.length}` : '—'}
          </Typography>
          {selectedIndex > 0 && (
            <Chip label="Latest" size="small" onClick={onLatest}
              sx={{ fontSize: 10, height: 18, bgcolor: 'rgba(255,255,255,0.12)',
                color: '#4fc3f7', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.25)' }} />
          )}
        </Box>
      </Box>
      <Box ref={ref} sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1,
        '&::-webkit-scrollbar': { height: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 2 } }}>
        {history.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 1 }}>
            <CircularProgress size={12} sx={{ color: '#4fc3f7' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Building history…</Typography>
          </Box>
        )}
        {history.map((item, idx) => (
          <Box key={idx} onClick={() => onSelect(idx)} sx={{
            flexShrink: 0, cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden',
            border: idx === selectedIndex ? '2px solid #4fc3f7' : '2px solid transparent',
            '&:hover': { border: '2px solid rgba(255,255,255,0.5)' },
          }}>
            <Box component="img" src={item.url} alt="" onError={(e) => { e.target.style.visibility = 'hidden'; }}
              sx={{ width: 80, height: 80, objectFit: 'cover', display: 'block', bgcolor: '#0d0d0d' }} />
            {idx === 0 && (
              <Box sx={{ position: 'absolute', top: 4, left: 4, bgcolor: '#ef5350', borderRadius: 0.5,
                px: 0.5, fontSize: 8, fontWeight: 800, color: '#fff', lineHeight: '14px', pointerEvents: 'none' }}>
                NEW
              </Box>
            )}
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center',
              fontSize: 9, color: 'text.secondary', lineHeight: 1.4, px: 0.5 }}>
              {fmtTime(item.timestamp)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Lightbox({ open, images, initialIndex, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (open) setIdx(initialIndex || 0); }, [open, initialIndex]);
  useEffect(() => {
    const h = (e) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx(i => Math.min((images?.length || 1) - 1, i + 1));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose, images]);

  if (!images?.length) return null;
  const cur = images[idx];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(2,8,16,0.97)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', p: { xs: 1, md: 3 }, zIndex: 9999 }}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16,
          bgcolor: 'rgba(255,255,255,0.1)', color: '#e3f2fd',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
          <Close />
        </IconButton>
        <Typography variant="caption" sx={{ position: 'absolute', top: 22,
          left: '50%', transform: 'translateX(-50%)',
          color: '#4fc3f7', fontFamily: "'Orbitron', monospace", fontSize: 10 }}>
          {idx + 1} / {images.length}
        </Typography>
        {idx > 0 && (
          <IconButton onClick={() => setIdx(i => i - 1)}
            sx={{ position: 'absolute', left: { xs: 4, md: 28 }, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.12)', color: '#4fc3f7',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <NavigateBefore sx={{ fontSize: 32 }} />
          </IconButton>
        )}
        <Box sx={{ maxWidth: '85vw', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box component="img" src={cur.url} alt={cur.title || ''}
            sx={{ maxWidth: '85vw', maxHeight: '68vh', objectFit: 'contain',
              borderRadius: 2, boxShadow: '0 0 60px rgba(255,255,255,0.15)' }} />
          <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
            <Typography variant="h6" sx={{ color: 'primary.light', fontWeight: 700, mb: 0.3 }}>
              {cur.title || 'Telescope Image'}
            </Typography>
            {cur.date && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                {cur.date}
              </Typography>
            )}
            {cur.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12,
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {cur.description}
              </Typography>
            )}
            <Button size="small" startIcon={<FileDownload />}
              onClick={() => downloadImage(cur.url, `telescope_${Date.now()}.jpg`)}
              sx={{ mt: 1.5, border: '1px solid rgba(255,255,255,0.3)', color: 'primary.light',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              Download
            </Button>
          </Box>
        </Box>
        {idx < images.length - 1 && (
          <IconButton onClick={() => setIdx(i => i + 1)}
            sx={{ position: 'absolute', right: { xs: 4, md: 28 }, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.12)', color: '#4fc3f7',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <NavigateNext sx={{ fontSize: 32 }} />
          </IconButton>
        )}
      </Box>
    </Modal>
  );
}

/* ══════════════════════════════════════════════ SDO TAB ══ */

function SDOTab({ onNotify }) {
  const [wl, setWl]             = useState(SDO_WAVELENGTHS[0]);
  const [history, setHistory]   = useState(() => SDO_WAVELENGTHS.map(w => [mkEntry(w.url, w.label)]));
  const [countdown, setCd]      = useState(900);
  const [refreshing, setRef]    = useState(false);
  const [selIdx, setSelIdx]     = useState(0);
  const [imgErr, setImgErr]     = useState(false);
  const [fs, setFs]             = useState(false);
  const [lb, setLb]             = useState({ open: false, idx: 0 });
  const containerRef            = useRef(null);

  const wIdx    = SDO_WAVELENGTHS.findIndex(w => w.key === wl.key);
  const curHist = history[wIdx] || [];
  const mainUrl = curHist[selIdx]?.url || wl.url;

  useEffect(() => {
    const h = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  const doRefresh = useCallback(() => {
    setRef(true); setImgErr(false);
    const entry = mkEntry(wl.url, wl.label);
    setHistory(prev => {
      const u = [...prev];
      u[wIdx] = [entry, ...u[wIdx]].slice(0, 60);
      return u;
    });
    setSelIdx(0);
    setTimeout(() => setRef(false), 1500);
    onNotify(`New image received from SDO · ${wl.label}`);
  }, [wl, wIdx, onNotify]);

  useEffect(() => {
    const iv = setInterval(() => setCd(c => { if (c <= 1) { doRefresh(); return 900; } return c - 1; }), 1000);
    return () => clearInterval(iv);
  }, [doRefresh]);

  const changeWl = (w) => { setWl(w); setSelIdx(0); setImgErr(false); setCd(900); };

  const toggleFs = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current.requestFullscreen();
  };

  const lbImages = curHist.map(h => ({
    url: h.url, title: `SDO · ${wl.label}`, date: h.timestamp?.toLocaleString(), description: wl.desc,
  }));

  return (
    <Box>
      <Grid container spacing={2.5}>
        {/* Main image */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden' }}>
            {/* Wavelength bar */}
            <Box sx={{ display: 'flex', gap: 1, p: 2, flexWrap: 'wrap', alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {SDO_WAVELENGTHS.map(w => (
                <Button key={w.key} size="small" onClick={() => changeWl(w)}
                  variant={wl.key === w.key ? 'contained' : 'outlined'}
                  sx={{ fontSize: 11, py: 0.4, px: 1.5, fontFamily: "'Orbitron', monospace",
                    ...(wl.key === w.key
                      ? { bgcolor: '#4fc3f7', color: '#000000', fontWeight: 700, '&:hover': { bgcolor: '#81d4fa' } }
                      : { borderColor: 'rgba(255,255,255,0.3)', color: '#4fc3f7' }) }}>
                  {w.label}
                </Button>
              ))}
              <Box sx={{ ml: 'auto' }}><CountdownChip seconds={countdown} refreshing={refreshing} /></Box>
            </Box>
            {/* Image */}
            <Box ref={containerRef} sx={{ position: 'relative', bgcolor: '#000000' }}>
              {refreshing && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, height: 2 }} />}
              {imgErr
                ? <ErrPlaceholder onRetry={() => { setImgErr(false); doRefresh(); }} />
                : (
                  <Box component="img" src={mainUrl} alt={`SDO ${wl.label}`}
                    onError={() => setImgErr(true)}
                    onClick={() => setLb({ open: true, idx: selIdx })}
                    sx={{ width: '100%', display: 'block', aspectRatio: '1/1', objectFit: 'cover',
                      cursor: 'zoom-in', filter: 'saturate(1.2) contrast(1.05)' }} />
                )}
              {/* Wave label overlay */}
              <Box sx={{ position: 'absolute', top: 12, left: 12,
                bgcolor: 'rgba(2,8,16,0.8)', borderRadius: 1, px: 1.5, py: 0.5,
                border: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography sx={{ fontSize: 11, color: '#4fc3f7',
                  fontFamily: "'Orbitron', monospace", fontWeight: 700 }}>
                  {wl.label} · {wl.desc}
                </Typography>
              </Box>
              {/* Action buttons */}
              <Box sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 1 }}>
                <Tooltip title="Download">
                  <IconButton size="small"
                    onClick={() => downloadImage(wl.url, `SDO_${wl.key}_${new Date().toISOString().split('T')[0]}.jpg`)}
                    sx={{ bgcolor: 'rgba(2,8,16,0.75)', color: '#4fc3f7',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={fs ? 'Exit Fullscreen' : 'Fullscreen'}>
                  <IconButton size="small" onClick={toggleFs}
                    sx={{ bgcolor: 'rgba(2,8,16,0.75)', color: '#4fc3f7',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                    {fs ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Updated: {curHist[0]?.timestamp?.toLocaleString() || new Date().toLocaleString()}
              </Typography>
              <Button size="small" startIcon={<Refresh sx={{ fontSize: '13px !important' }} />}
                onClick={doRefresh}
                sx={{ fontSize: 11, py: 0.3, color: '#4fc3f7',
                  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 1 }}>
                Refresh Now
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Side thumbnails */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase',
                letterSpacing: 1.5, fontSize: 10, display: 'block', mb: 2 }}>All Wavelengths</Typography>
              <Grid container spacing={1.5}>
                {SDO_WAVELENGTHS.map(w => (
                  <Grid item xs={6} key={w.key}>
                    <Box onClick={() => changeWl(w)} sx={{ cursor: 'pointer', borderRadius: 1.5,
                      overflow: 'hidden', transition: 'border 0.2s',
                      border: wl.key === w.key ? '2px solid #4fc3f7' : '2px solid rgba(255,255,255,0.12)',
                      '&:hover': { border: '2px solid rgba(255,255,255,0.45)' } }}>
                      <Box component="img"
                        src={`${w.url}?t=${Math.floor(Date.now() / 900000)}`}
                        alt={w.label}
                        sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.opacity = 0.3; }}
                      />
                      <Box sx={{ bgcolor: 'rgba(2,8,16,0.9)', p: 0.75, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 10, fontFamily: "'Orbitron', monospace", fontWeight: 700,
                          color: wl.key === w.key ? '#4fc3f7' : 'text.secondary' }}>
                          {w.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* History strip */}
      <Card sx={{ mt: 2.5, bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <HistoryStrip history={curHist} selectedIndex={selIdx}
            onSelect={setSelIdx} onLatest={() => setSelIdx(0)} label={`SDO · ${wl.label}`} />
        </CardContent>
      </Card>

      <Lightbox open={lb.open} images={lbImages} initialIndex={lb.idx}
        onClose={() => setLb({ open: false, idx: 0 })} />
    </Box>
  );
}

/* ═════════════════════════════════════════════ SOHO TAB ══ */

function SOHOTab({ solarWindSpeed, onNotify }) {
  const [view, setView]       = useState(SOHO_VIEWS[0]);
  const [history, setHistory] = useState(() =>
    SOHO_VIEWS.reduce((a, v) => ({ ...a, [v.key]: [mkEntry(v.url, v.label)] }), {}));
  const [countdown, setCd]    = useState(1800);
  const [refreshing, setRef]  = useState(false);
  const [selIdx, setSelIdx]   = useState(0);
  const [imgErr, setImgErr]   = useState(false);
  const [lb, setLb]           = useState({ open: false, idx: 0 });

  const curHist = history[view.key] || [];
  const mainUrl = curHist[selIdx]?.url || view.url;

  const doRefresh = useCallback(() => {
    setRef(true); setImgErr(false);
    const entry = mkEntry(view.url, view.label);
    setHistory(prev => ({ ...prev, [view.key]: [entry, ...(prev[view.key] || [])].slice(0, 60) }));
    setSelIdx(0);
    setTimeout(() => setRef(false), 1500);
    onNotify(`New image received from SOHO · ${view.label}`);
  }, [view, onNotify]);

  useEffect(() => {
    const iv = setInterval(() => setCd(c => { if (c <= 1) { doRefresh(); return 1800; } return c - 1; }), 1000);
    return () => clearInterval(iv);
  }, [doRefresh]);

  const changeView = (v) => { setView(v); setSelIdx(0); setImgErr(false); setCd(1800); };

  const lbImages = curHist.map(h => ({
    url: h.url, title: `SOHO · ${view.label}`, date: h.timestamp?.toLocaleString(),
    description: `Field of View: ${view.fov}`,
  }));

  return (
    <Box>
      {solarWindSpeed > 600 && (
        <Alert severity="warning" sx={{ mb: 2.5, bgcolor: 'rgba(255,167,38,0.1)',
          border: '1px solid rgba(255,167,38,0.35)',
          '& .MuiAlert-message': { color: '#ffa726' } }}>
          <strong>CME Detection Alert —</strong> Solar wind speed {solarWindSpeed} km/s exceeds 600 km/s threshold.
          Possible Coronal Mass Ejection. Monitor LASCO imagery closely.
        </Alert>
      )}

      {/* View selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {SOHO_VIEWS.map(v => (
          <Button key={v.key} size="small" onClick={() => changeView(v)}
            variant={view.key === v.key ? 'contained' : 'outlined'}
            sx={{ fontSize: 11, py: 0.4, px: 2, fontFamily: "'Orbitron', monospace",
              ...(view.key === v.key
                ? { bgcolor: v.color, color: '#000000', fontWeight: 700, '&:hover': { bgcolor: v.color, filter: 'brightness(1.1)' } }
                : { borderColor: `${v.color}55`, color: v.color }) }}>
            {v.label}
          </Button>
        ))}
        <Box sx={{ ml: 'auto' }}><CountdownChip seconds={countdown} refreshing={refreshing} /></Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: '#0d0d0d', border: `1px solid ${view.color}33`, borderRadius: 2, overflow: 'hidden' }}>
            {refreshing && <LinearProgress sx={{ height: 2 }} />}
            <Box sx={{ position: 'relative', bgcolor: '#000000' }}>
              {imgErr
                ? <ErrPlaceholder onRetry={() => { setImgErr(false); doRefresh(); }} />
                : (
                  <Box component="img" src={mainUrl} alt={view.label}
                    onError={() => setImgErr(true)}
                    onClick={() => setLb({ open: true, idx: selIdx })}
                    sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block',
                      cursor: 'zoom-in', filter: 'contrast(1.1)' }} />
                )}
              <Box sx={{ position: 'absolute', top: 12, left: 12,
                bgcolor: 'rgba(2,8,16,0.8)', borderRadius: 1, px: 1.5, py: 0.5,
                border: `1px solid ${view.color}44` }}>
                <Typography sx={{ fontSize: 11, color: view.color,
                  fontFamily: "'Orbitron', monospace", fontWeight: 700 }}>
                  {view.label} · FOV {view.fov}
                </Typography>
              </Box>
              <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
                <Tooltip title="Download">
                  <IconButton size="small"
                    onClick={() => downloadImage(view.url, `SOHO_${view.key}_${new Date().toISOString().split('T')[0]}.jpg`)}
                    sx={{ bgcolor: 'rgba(2,8,16,0.75)', color: view.color,
                      '&:hover': { bgcolor: `${view.color}22` } }}>
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Updated: {curHist[0]?.timestamp?.toLocaleString() || new Date().toLocaleString()}
              </Typography>
              <Button size="small" startIcon={<Refresh sx={{ fontSize: '13px !important' }} />}
                onClick={doRefresh}
                sx={{ fontSize: 11, py: 0.3, color: view.color,
                  border: `1px solid ${view.color}33`, borderRadius: 1 }}>
                Refresh Now
              </Button>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase',
                letterSpacing: 1.5, fontSize: 10, display: 'block', mb: 2 }}>All SOHO Views</Typography>
              <Grid container spacing={1.5}>
                {SOHO_VIEWS.map(v => (
                  <Grid item xs={6} key={v.key}>
                    <Box onClick={() => changeView(v)} sx={{ cursor: 'pointer', borderRadius: 1.5,
                      overflow: 'hidden', transition: 'border 0.2s',
                      border: view.key === v.key ? `2px solid ${v.color}` : '2px solid rgba(255,255,255,0.12)',
                      '&:hover': { border: `2px solid ${v.color}88` } }}>
                      <Box component="img"
                        src={`${v.url}?t=${Math.floor(Date.now() / 1800000)}`}
                        alt={v.label}
                        sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.opacity = 0.3; }}
                      />
                      <Box sx={{ bgcolor: 'rgba(2,8,16,0.9)', p: 0.75, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 10, fontFamily: "'Orbitron', monospace", fontWeight: 700,
                          color: view.key === v.key ? v.color : 'text.secondary' }}>{v.label}</Typography>
                        <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>{v.fov}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2.5, bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <HistoryStrip history={curHist} selectedIndex={selIdx}
            onSelect={setSelIdx} onLatest={() => setSelIdx(0)} label={`SOHO · ${view.label}`} />
        </CardContent>
      </Card>

      <Lightbox open={lb.open} images={lbImages} initialIndex={lb.idx}
        onClose={() => setLb({ open: false, idx: 0 })} />
    </Box>
  );
}

/* ════════════════════════════════════════ IMAGE GRID CARD ══ */

function ImgCard({ img, accentColor, onOpen, onDownload }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Card onClick={onOpen}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{ bgcolor: '#0d0d0d', border: `1px solid ${accentColor}18`, borderRadius: 2,
        overflow: 'hidden', height: '100%', cursor: 'pointer', transition: 'all 0.25s',
        '&:hover': { border: `1px solid ${accentColor}44`,
          boxShadow: `0 8px 24px ${accentColor}10`, transform: 'translateY(-2px)' } }}>
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src={img.url} alt={img.title}
          sx={{ width: '100%', aspectRatio: '1.4/1', objectFit: 'cover', display: 'block', bgcolor: '#000000' }}
          onError={(e) => {
            e.target.style.display = 'none';
            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
          }}
        />
        <Box sx={{ display: 'none', width: '100%', aspectRatio: '1.4/1',
          alignItems: 'center', justifyContent: 'center', bgcolor: '#000000', fontSize: 32 }}>🌌</Box>
        {img.isNew && (
          <Chip label="NEW" size="small" sx={{ position: 'absolute', top: 8, left: 8,
            fontSize: 9, height: 18, bgcolor: '#ef5350', color: '#fff', fontWeight: 800 }} />
        )}
        {hovered && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(2,8,16,0.78)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <IconButton size="small" sx={{ bgcolor: `${accentColor}22`, color: accentColor }}>
              <ZoomIn />
            </IconButton>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(); }}
              sx={{ bgcolor: `${accentColor}22`, color: accentColor }}>
              <FileDownload />
            </IconButton>
          </Box>
        )}
      </Box>
      <CardContent sx={{ p: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.3, fontSize: 12,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {img.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{img.date}</Typography>
      </CardContent>
    </Card>
  );
}

/* ════════════════════════════════════════════ JWST TAB ══ */

function JWSTTab({ onNotify }) {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [refreshing, setRef]  = useState(false);
  const [countdown, setCd]    = useState(1800);
  const [error, setError]     = useState(false);
  const [lb, setLb]           = useState({ open: false, idx: 0 });

  const fetchImages = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRef(true); else setLoading(true);
    setError(false);

    // Terms that indicate non-astronomy (press/event/engineering) images
    const BAD = [
      'briefing', 'conference', 'meeting', 'ceremony', 'administrator', 'coordinator',
      'technician', 'engineer', 'clean room', 'cleanroom', 'assembly', 'testing',
      'director', 'manager', 'senator', 'official', 'portrait', 'intern',
      'office ', 'facility', 'headquarters', 'contractor', 'award', 'tour',
      'delegation', 'personnel', 'workforce', 'reception', 'installation',
    ];
    // NASA Images API keyword tags that mark non-science photos
    const BAD_KW = ['People', 'Portrait', 'Events', 'Facilities', 'Awards'];
    // At least one of these must appear in title + description for the image to pass
    const GOOD = [
      'nebula', 'galaxy', 'galaxies', 'stellar', 'cosmic', 'infrared', 'deep field',
      'exoplanet', 'quasar', 'black hole', 'cluster', 'supernova', 'protostar',
      'interstellar', 'light-year', 'universe', 'parsec', 'star forming',
      'carina', 'pillars', 'tarantula', 'cartwheel', 'smacs', 'quintet',
      'wasp-', 'trappist', 'spectroscopy', 'webb image', 'webb captured',
      'webb reveals', 'billion light', 'million light', 'telescope captures',
      'southern ring', 'neptune', 'jupiter', 'mars', 'saturn', 'aurora',
    ];

    const isAstro = (title = '', desc = '', keywords = []) => {
      const text = `${title} ${desc}`.toLowerCase();
      if (BAD.some(w => text.includes(w))) return false;
      if (keywords.some(k => BAD_KW.includes(k))) return false;
      return GOOD.some(w => text.includes(w));
    };

    // Parse a raw NASA Images collection item → normalised object
    const toItem = (item) => {
      const meta        = item.data?.[0] || {};
      const nasaId      = meta.nasa_id || '';
      const previewLink = (item.links || []).find(l => l.rel === 'preview');
      const url         = previewLink?.href ||
        (nasaId ? `https://images-assets.nasa.gov/image/${nasaId}/${nasaId}~thumb.jpg` : '');
      const dateRaw     = meta.date_created ? new Date(meta.date_created) : new Date(0);
      return { id: nasaId || url, url, title: meta.title || 'JWST Image',
               date: dateRaw > new Date(0) ? dateRaw.toLocaleDateString() : 'Unknown',
               dateRaw, description: meta.description || '',
               keywords: meta.keywords || [], isNew: false };
    };

    // Astronomy-specific queries — each term forces results toward actual science images
    const QUERIES = ['james+webb+nebula', 'james+webb+galaxy', 'james+webb+infrared+deep'];
    const seen = new Set();
    let all = [];

    for (const q of QUERIES) {
      if (all.length >= 120) break;
      try {
        const res = await fetch(
          `https://images-api.nasa.gov/search?q=${q}&media_type=image&year_start=2022&page_size=100`
        );
        if (!res.ok) continue;
        const data = await res.json();
        for (const item of (data.collection?.items || [])) {
          const img = toItem(item);
          if (img.url && isAstro(img.title, img.description, img.keywords) && !seen.has(img.id)) {
            seen.add(img.id);
            all.push(img);
          }
        }
      } catch { /* try next query */ }
    }

    // Sort newest-first by date_created
    all.sort((a, b) => b.dateRaw - a.dateRaw);
    const items = all.slice(0, 60);

    if (items.length === 0) {
      setError(true);
    } else if (isRefresh) {
      setImages(prev => {
        const prevIds  = new Set(prev.map(i => i.id));
        const newestTs = prev[0]?.dateRaw ?? new Date(0);
        const fresh    = items.filter(i => !prevIds.has(i.id) && i.dateRaw > newestTs);
        if (!fresh.length) return prev;
        return [...fresh.map((i, k) => ({ ...i, isNew: k === 0 })), ...prev].slice(0, 60);
      });
      onNotify('New JWST image available');
    } else {
      setImages(items);
    }

    setLoading(false);
    setRef(false);
  }, [onNotify]);

  useEffect(() => { fetchImages(); }, [fetchImages]);
  useEffect(() => {
    const iv = setInterval(() => setCd(c => { if (c <= 1) { fetchImages(true); return 1800; } return c - 1; }), 1000);
    return () => clearInterval(iv);
  }, [fetchImages]);

  const filtered = useMemo(() => {
    if (!search.trim()) return images;
    const q = search.toLowerCase();
    return images.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  }, [images, search]);

  const lbImages = filtered.map(i => ({ url: i.url, title: i.title, date: i.date, description: i.description }));

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12 }}>
      <CircularProgress sx={{ color: '#4fc3f7', mb: 2 }} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading JWST images from NASA…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ textAlign: 'center', py: 12 }}>
      <Typography sx={{ fontSize: 48, mb: 2 }}>🌌</Typography>
      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>Unable to load JWST images</Typography>
      <Button variant="outlined" onClick={() => fetchImages()}
        sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light' }}>Retry</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField placeholder="Search JWST images…" value={search}
          onChange={e => setSearch(e.target.value)} size="small" sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment> }} />
        <CountdownChip seconds={countdown} refreshing={refreshing} />
        <Button size="small" startIcon={<Refresh />} onClick={() => fetchImages(true)}
          sx={{ border: '1px solid rgba(255,255,255,0.3)', color: 'primary.light', fontSize: 11, py: 0.5 }}>
          Refresh
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}>
        {filtered.length} images · James Webb Space Telescope · NASA Archive
      </Typography>
      <Grid container spacing={2}>
        {filtered.map((img, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
            <ImgCard img={img} accentColor="#4fc3f7"
              onOpen={() => setLb({ open: true, idx })}
              onDownload={() => downloadImage(img.url, `JWST_${img.id}.jpg`)} />
          </Grid>
        ))}
      </Grid>
      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontSize: 40, mb: 1 }}>🔍</Typography>
          <Typography sx={{ color: 'text.secondary' }}>No images match your search</Typography>
        </Box>
      )}
      <Lightbox open={lb.open} images={lbImages} initialIndex={lb.idx}
        onClose={() => setLb({ open: false, idx: 0 })} />
    </Box>
  );
}

/* ══════════════════════════════════════════ HUBBLE TAB ══ */

function HubbleTab({ onNotify }) {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('All');
  const [refreshing, setRef]  = useState(false);
  const [countdown, setCd]    = useState(1800);
  const [error, setError]     = useState(false);
  const [lb, setLb]           = useState({ open: false, idx: 0 });

  const fetchImages = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRef(true); else setLoading(true);
    setError(false);

    const BAD = [
      'briefing', 'conference', 'meeting', 'ceremony', 'administrator', 'coordinator',
      'technician', 'engineer', 'clean room', 'cleanroom', 'assembly', 'testing',
      'director', 'manager', 'senator', 'official', 'portrait', 'intern',
      'office ', 'facility', 'headquarters', 'contractor', 'award', 'tour',
      'delegation', 'personnel', 'workforce', 'reception', 'installation',
    ];
    const BAD_KW = ['People', 'Portrait', 'Events', 'Facilities', 'Awards'];
    const GOOD = [
      'nebula', 'galaxy', 'galaxies', 'stellar', 'cosmic', 'infrared', 'deep field',
      'exoplanet', 'quasar', 'black hole', 'cluster', 'supernova', 'protostar',
      'interstellar', 'light-year', 'universe', 'parsec', 'star forming',
      'carina', 'pillars', 'tarantula', 'cartwheel', 'globular', 'emission',
      'nebular', 'orion', 'andromeda', 'magellanic', 'helix', 'eagle',
      'southern ring', 'neptune', 'jupiter', 'saturn', 'aurora', 'billion',
    ];

    const isAstro = (title = '', desc = '', keywords = []) => {
      const text = `${title} ${desc}`.toLowerCase();
      if (BAD.some(w => text.includes(w))) return false;
      if (keywords.some(k => BAD_KW.includes(k))) return false;
      return GOOD.some(w => text.includes(w));
    };

    const toItem = (item) => {
      const meta        = item.data?.[0] || {};
      const nasaId      = meta.nasa_id || '';
      const previewLink = (item.links || []).find(l => l.rel === 'preview');
      const url         = previewLink?.href ||
        (nasaId ? `https://images-assets.nasa.gov/image/${nasaId}/${nasaId}~thumb.jpg` : '');
      const dateRaw     = meta.date_created ? new Date(meta.date_created) : new Date(0);
      return { id: nasaId || url, url, title: meta.title || 'Hubble Image',
               date: dateRaw > new Date(0) ? dateRaw.toLocaleDateString() : 'Unknown',
               dateRaw, description: meta.description || '',
               keywords: meta.keywords || [],
               category: (meta.keywords || []).join(' '), isNew: false };
    };

    let items = [];

    // ── Source 1: HubbleSite API — guaranteed Hubble science images ──
    try {
      const res = await fetch('https://hubblesite.org/api/v3/image?page=1&per_page=60&Type=News+Release');
      if (res.ok) {
        const data = await res.json();
        const arr  = Array.isArray(data) ? data : (data.images || []);
        const mapped = arr
          .filter(i => i.thumbnail_url && i.name)
          .map(i => ({
            id:          String(i.id),
            url:         i.thumbnail_url,
            title:       i.name || 'Hubble Image',
            date:        i.news_name || 'Unknown',
            dateRaw:     i.news_name ? new Date(`${i.news_name.slice(0, 4)}-01-01`) : new Date(0),
            description: i.description || '',
            keywords:    [],
            category:    i.collection || '',
            isNew:       false,
          }))
          .sort((a, b) => Number(b.id) - Number(a.id))
          .slice(0, 60);
        if (mapped.length > 0) items = mapped;
      }
    } catch { /* fall through */ }

    // ── Source 2: NASA Images API with astronomy-specific queries ──
    if (items.length === 0) {
      const QUERIES = ['hubble+nebula', 'hubble+galaxy', 'hubble+cluster+stars'];
      const seen = new Set();
      let all = [];
      for (const q of QUERIES) {
        if (all.length >= 120) break;
        try {
          const res = await fetch(
            `https://images-api.nasa.gov/search?q=${q}&media_type=image&year_start=2015&page_size=100`
          );
          if (!res.ok) continue;
          const data = await res.json();
          for (const item of (data.collection?.items || [])) {
            const img = toItem(item);
            if (img.url && isAstro(img.title, img.description, img.keywords) && !seen.has(img.id)) {
              seen.add(img.id);
              all.push(img);
            }
          }
        } catch { /* next */ }
      }
      all.sort((a, b) => b.dateRaw - a.dateRaw);
      items = all.slice(0, 60);
    }

    if (items.length === 0) {
      setError(true);
    } else if (isRefresh) {
      setImages(prev => {
        const prevIds  = new Set(prev.map(i => i.id));
        const newestTs = prev[0]?.dateRaw ?? new Date(0);
        const fresh    = items.filter(i => !prevIds.has(i.id) && i.dateRaw > newestTs);
        if (!fresh.length) return prev;
        return [...fresh.map((i, k) => ({ ...i, isNew: k === 0 })), ...prev].slice(0, 60);
      });
      onNotify('New Hubble image available');
    } else {
      setImages(items);
    }

    setLoading(false);
    setRef(false);
  }, [onNotify]);

  useEffect(() => { fetchImages(); }, [fetchImages]);
  useEffect(() => {
    const iv = setInterval(() => setCd(c => { if (c <= 1) { fetchImages(true); return 1800; } return c - 1; }), 1000);
    return () => clearInterval(iv);
  }, [fetchImages]);

  const filtered = useMemo(() => {
    let r = images;
    if (filter !== 'All') {
      const q = filter.toLowerCase();
      r = r.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q) || i.category.toLowerCase().includes(q));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
    }
    return r;
  }, [images, filter, search]);

  const lbImages = filtered.map(i => ({ url: i.url, title: i.title, date: i.date, description: i.description }));

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12 }}>
      <CircularProgress sx={{ color: '#ab47bc', mb: 2 }} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading Hubble images…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ textAlign: 'center', py: 12 }}>
      <Typography sx={{ fontSize: 48, mb: 2 }}>🔭</Typography>
      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>Unable to load Hubble images</Typography>
      <Button variant="outlined" onClick={() => fetchImages()}
        sx={{ borderColor: 'rgba(171,71,188,0.3)', color: '#ab47bc' }}>Retry</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField placeholder="Search Hubble images…" value={search}
          onChange={e => setSearch(e.target.value)} size="small" sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment> }} />
        <CountdownChip seconds={countdown} refreshing={refreshing} />
        <Button size="small" startIcon={<Refresh />} onClick={() => fetchImages(true)}
          sx={{ border: '1px solid rgba(171,71,188,0.3)', color: '#ab47bc', fontSize: 11, py: 0.5 }}>Refresh</Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {HUBBLE_FILTERS.map(f => (
          <Chip key={f} label={f} clickable onClick={() => setFilter(f)}
            variant={filter === f ? 'filled' : 'outlined'}
            sx={{ fontSize: 12, fontWeight: 600,
              ...(filter === f
                ? { bgcolor: 'rgba(171,71,188,0.2)', color: '#ab47bc', border: '1px solid rgba(171,71,188,0.45)' }
                : { borderColor: 'rgba(171,71,188,0.2)', color: 'text.secondary',
                    '&:hover': { borderColor: 'rgba(171,71,188,0.4)' } }) }} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}>
        {filtered.length} images · Hubble Space Telescope
      </Typography>
      <Grid container spacing={2}>
        {filtered.map((img, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
            <ImgCard img={img} accentColor="#ab47bc"
              onOpen={() => setLb({ open: true, idx })}
              onDownload={() => downloadImage(img.url, `Hubble_${img.id}.jpg`)} />
          </Grid>
        ))}
      </Grid>
      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontSize: 40, mb: 1 }}>🔍</Typography>
          <Typography sx={{ color: 'text.secondary' }}>No images match your filters</Typography>
        </Box>
      )}
      <Lightbox open={lb.open} images={lbImages} initialIndex={lb.idx}
        onClose={() => setLb({ open: false, idx: 0 })} />
    </Box>
  );
}

/* ═══════════════════════════════════ LIVE STREAMS TAB ══ */

/* ══════════════════════════════════════════ MAIN PAGE ══ */

const TABS = ['☀️ NASA SDO', '🌞 SOHO', '🌌 James Webb', '🔭 Hubble'];

export default function TelescopeFeedPage() {
  const [tab, setTab]         = useState(0);
  const [stats, setStats]     = useState({ flareClass: '--', windSpeed: '--', kpIndex: '--', sunspots: '--', windRaw: 0 });
  const [statsLoad, setSL]    = useState(true);
  const [notif, setNotif]     = useState({ open: false, msg: '' });

  const notify = useCallback((msg) => setNotif({ open: true, msg }), []);

  // Load Orbitron font
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&display=swap';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch {} };
  }, []);

  const fetchStats = useCallback(async () => {
    setSL(true);
    try {
      const [xR, wR, kR, rR] = await Promise.allSettled([
        fetch('https://services.swpc.noaa.gov/json/goes/primary/xrays-1-day.json'),
        fetch('https://services.swpc.noaa.gov/json/rtsw/rtsw_wind_1m.json'),
        fetch('https://services.swpc.noaa.gov/json/planetary_k_index_1m.json'),
        fetch('https://services.swpc.noaa.gov/json/solar_regions.json'),
      ]);

      let flareClass = '--', windSpeed = '--', kpIndex = '--', sunspots = '--', windRaw = 0;

      if (xR.status === 'fulfilled') {
        const d = await xR.value.json();
        const lw = d.filter(x => x.energy === '0.1-0.8nm');
        if (lw.length) flareClass = flareFromFlux(lw[lw.length - 1].flux);
      }
      if (wR.status === 'fulfilled') {
        const d = await wR.value.json();
        if (d.length) {
          const v = d[d.length - 1];
          windRaw = Math.round(v.proton_speed || v.speed || 0);
          if (windRaw > 0) windSpeed = windRaw;
        }
      }
      if (kR.status === 'fulfilled') {
        const d = await kR.value.json();
        if (d.length) {
          const v = d[d.length - 1];
          kpIndex = parseFloat(v.kp_index ?? v.Kp ?? 0).toFixed(1);
        }
      }
      if (rR.status === 'fulfilled') {
        const d = await rR.value.json();
        sunspots = Array.isArray(d) ? d.length : '--';
      }

      setStats({ flareClass, windSpeed, kpIndex, sunspots, windRaw });
    } catch { /* keep defaults */ }
    finally { setSL(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fc = flareColor(stats.flareClass);
  const kpVal = parseFloat(stats.kpIndex);
  const kpColor = kpVal >= 5 ? '#ef5350' : kpVal >= 3 ? '#ffa726' : '#66bb6a';
  const windColor = stats.windRaw > 600 ? '#ef5350' : '#4fc3f7';

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <GlobalStyles styles={`
        @keyframes tp-pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.35; transform:scale(0.65); }
        }
      `} />

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{
            fontWeight: 900, color: '#e3f2fd', mb: 0.5,
            fontFamily: "'Orbitron', monospace",
            textShadow: '0 0 40px rgba(255,255,255,0.35)',
          }}>
            🔭 Telescope Live Feed
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Real-time imagery from NASA SDO · SOHO · Hubble · JWST
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { label: 'SDO Online',    color: '#66bb6a' },
              { label: 'SOHO Online',   color: '#66bb6a' },
              { label: 'JWST Loaded',   color: '#4fc3f7' },
              { label: 'Hubble Loaded', color: '#ab47bc' },
            ].map(s => (
              <Chip key={s.label} label={s.label} size="small"
                icon={<Circle sx={{ fontSize: '8px !important', color: `${s.color} !important` }} />}
                sx={{ fontSize: 11, height: 24, bgcolor: `${s.color}15`,
                  border: `1px solid ${s.color}44`, color: s.color }} />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <LiveBadge />
          <Button size="small" startIcon={<Refresh sx={{ fontSize: '13px !important' }} />}
            onClick={fetchStats}
            sx={{ fontSize: 11, border: '1px solid rgba(255,255,255,0.25)',
              color: 'primary.light', borderRadius: 1 }}>
            Refresh Stats
          </Button>
        </Box>
      </Box>

      {/* ── STATS ROW ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<FlashOn sx={{ color: fc, fontSize: 22 }} />}
            label="Latest Solar Flare" value={stats.flareClass} color={fc} loading={statsLoad} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<Air sx={{ color: windColor, fontSize: 22 }} />}
            label="Solar Wind Speed" value={stats.windSpeed} unit="km/s" color={windColor} loading={statsLoad} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<WbSunny sx={{ color: kpColor, fontSize: 22 }} />}
            label="Kp Index" value={stats.kpIndex} color={kpColor} loading={statsLoad} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<Grain sx={{ color: '#ffa726', fontSize: 22 }} />}
            label="Active Sunspot Regions" value={stats.sunspots} color="#ffa726" loading={statsLoad} />
        </Grid>
      </Grid>

      {/* ── TAB BAR ── */}
      <Card sx={{ mb: 3, bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            '& .MuiTabs-indicator': { bgcolor: '#4fc3f7', height: 3, borderRadius: '3px 3px 0 0' },
            '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, fontSize: 13,
              textTransform: 'none', minWidth: { xs: 110, sm: 140 },
              '&.Mui-selected': { color: '#4fc3f7' } },
          }}>
          {TABS.map((t, i) => <Tab key={i} label={t} />)}
        </Tabs>
      </Card>

      {/* ── TAB PANELS ── */}
      {tab === 0 && <SDOTab onNotify={notify} />}
      {tab === 1 && <SOHOTab solarWindSpeed={stats.windRaw} onNotify={notify} />}
      {tab === 2 && <JWSTTab onNotify={notify} />}
      {tab === 3 && <HubbleTab onNotify={notify} />}

      {/* ── FOOTER ── */}
      <Box sx={{ textAlign: 'center', mt: 5, pb: 3 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          SpacePulse Telescope Feed · NASA SDO · SOHO/NASCOM · NASA Images API · NOAA SWPC · Data refreshes automatically
        </Typography>
      </Box>

      {/* ── TOAST ── */}
      <Snackbar open={notif.open} autoHideDuration={4000}
        onClose={() => setNotif(n => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="info" onClose={() => setNotif(n => ({ ...n, open: false }))}
          sx={{ bgcolor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.3)', color: '#4fc3f7',
            '& .MuiAlert-icon': { color: '#4fc3f7' } }}>
          {notif.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
